// CallModalStack.tsx
import { Drawer } from 'antd';
import { CALLER_ID_DRAWER_INDEX } from 'constants/variables';
import { useCallback, useMemo, useState } from 'react';
import type { ICustomerUI } from 'types/customer';
import CallerIdCard from './CallerIdCard';

type CallItem = {
  callId: string;
  customer: ICustomerUI;
  createdAt: number;
  isClosing?: boolean;
};

export type CallerIdApi = {
  open: (call: { callId: string; customer: ICustomerUI }) => void;
  close: (callId: string) => void;
  update: (call: { callId: string; customer?: ICustomerUI }) => void;
};

let apiRef: CallerIdApi | null = null;

export function CallModalStack() {
  const [calls, setCalls] = useState<CallItem[]>([]);

  const open = useCallback((args: { callId: string; customer: ICustomerUI }) => {
    setCalls((prev) => {
      // nếu trùng callId thì update thay vì tạo modal mới
      const idx = prev.findIndex((c) => c.callId === args.callId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], customer: args.customer };
        return next;
      }
      return [{ callId: args.callId, customer: args.customer, createdAt: Date.now() }, ...prev];
    });
  }, []);

  const close = useCallback((callId: string) => {
    // Mark item as closing to trigger animation
    setCalls((prev) =>
      prev.map((c) => (c.callId === callId ? { ...c, isClosing: true } : c))
    );
  }, []);

  const removeCall = useCallback((callId: string) => {
    // Actually remove the item after animation
    setCalls((prev) => prev.filter((c) => c.callId !== callId));
  }, []);

  const update = useCallback((args: { callId: string; customer?: ICustomerUI }) => {
    setCalls((prev) =>
      prev.map((c) => (c.callId === args.callId ? { ...c, customer: args.customer ?? c.customer } : c))
    );
  }, []);

  // expose singleton API
  useMemo(() => {
    apiRef = { open, close, update };
    return null;
  }, [open, close, update]);

  return (
    <Drawer
      open={calls.length > 0}
      footer={null}
      closable={false}
      width={'auto'}
      destroyOnClose
      mask
      placement="right"
      bodyStyle={{ padding: 0, background: 'transparent' }}
      styles={{ content: { background: 'transparent' }, body: { boxShadow: 'none' } }}
      contentWrapperStyle={{ boxShadow: 'none' }}
      zIndex={CALLER_ID_DRAWER_INDEX}
    >
      <div style={{ display: 'flex', flexDirection: 'column', padding: 12, gap: 16 }}>
        {calls.map((call) => (
          <div
            key={call.callId}
            className={call.isClosing ? 'caller-card-wrapper closing' : 'caller-card-wrapper'}
            onAnimationEnd={() => {
              if (call.isClosing) {
                removeCall(call.callId);
              }
            }}
          >
            <CallerIdCard
              callerId={call.callId}
              customer={call.customer}
              onClose={() => close(call.callId)}
              createdAt={call.createdAt}
            />
          </div>
        ))}
      </div>
      <style>{`
        .caller-card-wrapper {
          animation: slideIn 0.3s ease-out;
        }
        
        .caller-card-wrapper.closing {
          animation: slideOut 0.3s ease-in forwards;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `}</style>
    </Drawer>
  );
}

const CallerId = {
  open: (args: { callId: string; customer: ICustomerUI }) => apiRef?.open(args),
  close: (callId: string) => apiRef?.close(callId),
  update: (args: { callId: string; customer?: ICustomerUI }) => apiRef?.update(args),
};

export default CallerId;
